import { cn } from '@/lib/client'
import { useStore } from '@nanostores/react'
import { CircleCheck, CircleX, RotateCw } from 'lucide-react'
import type { WritableAtom } from 'nanostores'
import { useEffect } from 'react'
import { Button, type ButtonProps } from './button'

interface CustomButtonProps extends ButtonProps {
  loadingAtom: WritableAtom<'idle' | 'loading' | 'succeed' | 'failed'>
  succeed?: string
  failed?: string
}

export function ButtonLoading({
  loadingAtom,
  className,
  succeed = 'Concluído',
  failed = 'Erro',
  ...props
}: CustomButtonProps & React.ComponentPropsWithoutRef<'button'>) {
  const $loading = useStore(loadingAtom)

  useEffect(() => {
    if (['succeed', 'failed'].includes($loading)) {
      const timeout = setTimeout(() => loadingAtom.set('idle'), 3000)
      return () => clearTimeout(timeout)
    }
  }, [$loading])

  return (
    <Button
      type="button"
      variant="default"
      className={cn(
        'relative flex items-center justify-center w-full px-6 py-2 font-medium transition-all duration-300 disabled:opacity-100 disabled:cursor-not-allowed',
        className,
        $loading === 'succeed' &&
          'border border-emerald-600 text-emerald-600 bg-zinc-100 hover:bg-zinc-100',
        $loading === 'failed' &&
          'border border-rose-600 text-rose-600 bg-zinc-100 hover:bg-zinc-100',
        $loading === 'loading' && 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300',
      )}
      disabled={$loading !== 'idle' || props.disabled}
      {...props}
    >
      <div className="relative w-full flex items-center justify-center">
        {/* Idle state */}
        <span
          className={cn(
            'absolute transition-all duration-300 ease-in-out flex items-center text-primary-foreground',
            $loading === 'idle'
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 -translate-y-3',
          )}
        >
          {props.children}
        </span>

        {/* Loading state */}
        <div
          className={cn(
            'absolute transition-all duration-300 ease-in-out flex items-center justify-center',
            $loading === 'loading'
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 -translate-y-3',
          )}
        >
          <RotateCw className="w-5 h-5 loading text-zinc-600 animate-spin" />
        </div>

        {/* Success state */}
        <div
          className={cn(
            'absolute transition-all duration-300 ease-in-out flex items-center gap-1 text-emerald-600',
            $loading === 'succeed'
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 -translate-y-3',
          )}
        >
          <CircleCheck className="w-5 h-5 text-zinc-50 fill-emerald-600" />
          <span>{succeed}</span>
        </div>

        {/* Failed state */}
        <div
          className={cn(
            'absolute transition-all duration-300 ease-in-out flex items-center gap-1 text-rose-600',
            $loading === 'failed'
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 -translate-y-3',
          )}
        >
          <CircleX className="w-5 h-5 text-zinc-50 fill-rose-600" />
          <span>{failed}</span>
        </div>
      </div>
    </Button>
  )
}
